import {
  Accordion,
  Checkbox,
  AccordionDetails,
  Grid,
  Typography,
  FormControlLabel,
  useEventCallback,
} from '@mui/material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import { CirculationAreaForm } from '.';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import AreaType from './type';
import DeliberatedType from './type-deliberated';
import PmiTextField from 'shell/components/shared/text-field';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { FormikErrors, FormikTouched } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { noop } from 'shell/utils/noop';
import { AbilityChangesType } from 'system/pages/toponymy/circulation-area/edit';

interface FormAreaProps {
  title: string;
  disabled?: boolean;
  circulationAreaForm?: CirculationAreaForm;
  handleChange: (key: string, value: string | boolean | DateTime) => void;
  handleChanges: (newValues: CirculationAreaForm) => void;
  newArea: boolean;
  errors?: FormikErrors<CirculationAreaForm>;
  touched?: FormikTouched<CirculationAreaForm>;
  abilityChanges?: AbilityChangesType;
  setAbilityChanges?: (newState: AbilityChangesType) => void;
  onSaveCirculationArea?: (values: CirculationAreaForm) => void;
}

const FormArea = ({
  title,
  disabled = false,
  circulationAreaForm,
  handleChange,
  handleChanges,
  newArea,
  errors,
  touched,
  abilityChanges,
  setAbilityChanges = noop,
  onSaveCirculationArea = noop,
}: FormAreaProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(true);
  const [queryParams] = useSearchParams();

  const civic = queryParams.get('civico') || '';

  const disabledComponent: boolean = useMemo(() => {
    if (abilityChanges?.isAbilityChanges) {
      return false;
    }

    return disabled;
  }, [newArea, abilityChanges?.isAbilityChanges]);

  useEffect(() => {
    setOpen(!civic);
  }, []);

  const handleChangeDeliberated = useCallback(
    (event) => {
      handleChange('typeDeliberated', event.target.value as string);
    },
    [handleChange]
  );

  const handleChangeAreaType = useCallback(
    (event) => {
      handleChange('type', event.target.value as string);
    },
    [handleChange]
  );

  const onAbilityChanges = useEventCallback(() => {
    setAbilityChanges({
      isAbilityChanges: true,
      formValues: circulationAreaForm,
    });
  });

  const onDisabilityChanges = useEventCallback(() => {
    handleChanges({
      ...(abilityChanges?.formValues || {}),
    } as CirculationAreaForm);

    setAbilityChanges({
      isAbilityChanges: false,
      formValues: undefined,
    });
  });

  const handleChangeTerminate = useEventCallback(() => {
    handleChanges({
      ...(circulationAreaForm || {}),
      terminate: !circulationAreaForm?.terminate,
      deliberated: false,
    } as CirculationAreaForm);
  });

  const handleChangeFlagDeliberated = useEventCallback(() => {
    handleChanges({
      ...(circulationAreaForm || {}),
      terminate: false,
      deliberated: !circulationAreaForm?.deliberated,
    } as CirculationAreaForm);
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-area-content"
        id="panel-area-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div className="pmi-mt-2">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <PmiTextField
                label={newArea ? 'Codice *' : 'Codice'}
                id="code"
                name="code"
                fullWidth
                type="text"
                variant="outlined"
                value={circulationAreaForm?.code || ''}
                disabled={!newArea}
                onChange={(event) => {
                  handleChange('code', event.target.value);
                }}
                helperText={errors?.code}
                error={!!errors?.code && touched?.code}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <AreaType
                disabled={disabledComponent}
                value={circulationAreaForm?.type || ''}
                onChange={handleChangeAreaType}
                error={!!errors?.type && touched?.type}
                required={newArea}
              />
            </Grid>
            {!newArea ? (
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="terminate"
                      name="terminate"
                      disabled={disabledComponent}
                      checked={circulationAreaForm?.terminate || false}
                      inputProps={{ 'aria-label': 'cessato' }}
                      onChange={() => handleChangeTerminate()}
                    />
                  }
                  label="Cessato"
                />
              </Grid>
            ) : undefined}
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    id="deliberated"
                    name="deliberated"
                    disabled={disabledComponent}
                    checked={circulationAreaForm?.deliberated || false}
                    onChange={() => handleChangeFlagDeliberated()}
                    inputProps={{ 'aria-label': 'non deliberato' }}
                  />
                }
                label="Non deliberato"
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <PmiTextField
                label={newArea ? 'Nome *' : 'Nome'}
                id="name"
                name="name"
                fullWidth
                type="text"
                variant="outlined"
                value={circulationAreaForm?.name || ''}
                onChange={(event) => {
                  handleChange('name', event.target.value);
                }}
                disabled={disabledComponent}
                helperText={errors?.name}
                error={!!errors?.name && touched?.name}
              />
            </Grid>
            {newArea ? undefined : (
              <Fragment>
                <Grid item xs={12} md={6}>
                  <PmiTextField
                    label="Descrizione postale ridotta"
                    id="descriptionReducedPostal"
                    name="descriptionReducedPostal"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={circulationAreaForm?.descriptionReducedPostal || ''}
                    onChange={(event) => {
                      handleChange('descriptionReducedPostal', event.target.value);
                    }}
                    disabled={disabledComponent}
                    helperText={errors?.descriptionReducedPostal}
                    error={!!errors?.descriptionReducedPostal && touched?.descriptionReducedPostal}
                    inputProps={{
                      maxLength: '30',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PmiTextField
                    label="Descrizione ridotta"
                    id="descriptionReduced"
                    name="descriptionReduced"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={circulationAreaForm?.descriptionReduced || ''}
                    onChange={(event) => {
                      handleChange('descriptionReduced', event.target.value);
                    }}
                    disabled={disabledComponent}
                    helperText={errors?.descriptionReduced}
                    error={!!errors?.descriptionReduced && touched?.descriptionReduced}
                    inputProps={{
                      maxLength: '20',
                    }}
                  />
                </Grid>
              </Fragment>
            )}
            <Grid item xs={12} md={newArea ? 12 : 6}>
              <DatePicker
                label={newArea ? 'Data Istituzione *' : 'Data Istituzione'}
                value={circulationAreaForm?.estabilishmentDate || null}
                disabled={disabledComponent}
                onChange={(date) => handleChange('estabilishmentDate', date || '')}
                renderInput={(params) => (
                  <PmiTextField
                    id="estabilishmentDate"
                    name="estabilishmentDate"
                    {...params}
                    helperText={errors?.estabilishmentDate}
                    error={!!errors?.estabilishmentDate && touched?.estabilishmentDate}
                  />
                )}
              />
            </Grid>
            {!newArea ? (
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Data Cessazione"
                  value={circulationAreaForm?.endDate || null}
                  disabled={disabledComponent}
                  onChange={(date) => handleChange('endDate', date || '')}
                  renderInput={(params) => (
                    <PmiTextField
                      id="endDate"
                      name="endDate"
                      {...params}
                      error={false}
                      helperText=""
                    />
                  )}
                />
              </Grid>
            ) : undefined}
            <Grid item xs={12} md={6}>
              <DeliberatedType
                disabled={disabledComponent}
                value={circulationAreaForm?.typeDeliberated || ''}
                onChange={handleChangeDeliberated}
                required={newArea}
                error={!!errors?.typeDeliberated && touched?.typeDeliberated}
              />
            </Grid>
            {!newArea ? (
              <Grid item xs={12} display="flex" justifyContent="center">
                <PmiButton
                  variant="contained"
                  color="primary"
                  onClick={onAbilityChanges}
                  disabled={abilityChanges?.isAbilityChanges}
                  style={{ marginRight: '1rem' }}
                >
                  Abilita modifiche
                </PmiButton>
                {abilityChanges?.isAbilityChanges ? (
                  <Fragment>
                    <PmiButton
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        onSaveCirculationArea((circulationAreaForm || {}) as CirculationAreaForm)
                      }
                      style={{ marginRight: '1rem' }}
                    >
                      Salva modifiche
                    </PmiButton>
                    <PmiButton color="cdmBrand" variant="outlined" onClick={onDisabilityChanges}>
                      Annulla modifiche
                    </PmiButton>
                  </Fragment>
                ) : undefined}
              </Grid>
            ) : undefined}
          </Grid>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

FormArea.displayName = 'Form Area component';

export default FormArea;
